import React, { useState, useEffect, useMemo } from 'react';
import {
  Search, Loader2, ArrowUpDown, ArrowUp, ArrowDown,
  Globe, BarChart3, Target, TrendingUp, Filter, Download, ChevronDown
} from 'lucide-react';
import { fetchBrandsForKeywordGap, analyzeKeywordGap, formatNumber } from '../services/keywordGapService';

const KeywordGapAnalyser = () => {
  const [brands, setBrands] = useState([]);
  const [selectedBrandUrl, setSelectedBrandUrl] = useState('');
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [database, setDatabase] = useState('uk');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('missing');
  const [sortConfig, setSortConfig] = useState({ key: 'searchVolume', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 25;

  const databases = [
    { code: 'uk', name: 'United Kingdom' },
    { code: 'us', name: 'United States' },
    { code: 'au', name: 'Australia' },
    { code: 'ca', name: 'Canada' },
    { code: 'de', name: 'Germany' },
    { code: 'fr', name: 'France' },
    { code: 'es', name: 'Spain' },
    { code: 'it', name: 'Italy' },
    { code: 'br', name: 'Brazil' },
    { code: 'in', name: 'India' },
    { code: 'nl', name: 'Netherlands' },
    { code: 'se', name: 'Sweden' },
    { code: 'no', name: 'Norway' },
    { code: 'dk', name: 'Denmark' },
    { code: 'fi', name: 'Finland' },
    { code: 'nz', name: 'New Zealand' },
    { code: 'ie', name: 'Ireland' },
    { code: 'za', name: 'South Africa' },
    { code: 'pl', name: 'Poland' },
    { code: 'ro', name: 'Romania' },
  ];

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      const response = await fetchBrandsForKeywordGap();
      if (Array.isArray(response)) {
        setBrands(response);
      } else if (response && Array.isArray(response.data)) {
        setBrands(response.data);
      } else if (response && typeof response === 'object') {
        const possibleArrays = Object.values(response).filter(v => Array.isArray(v));
        setBrands(possibleArrays.length > 0 ? possibleArrays[0] : []);
      } else {
        setBrands([]);
      }
    } catch (err) {
      console.error('Error loading brands:', err);
      setBrands([]);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedBrandUrl || !competitorUrl.trim()) {
      setError('Please select a brand and enter a competitor URL');
      return;
    }

    setError('');
    setIsLoading(true);
    setResults(null);
    setCurrentPage(0);

    try {
      const response = await analyzeKeywordGap(selectedBrandUrl, competitorUrl.trim(), database);
      if (response.success) {
        setResults(response.data);
        setActiveTab('missing');
      } else {
        setError(response.message || 'Analysis failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to perform keyword gap analysis');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-3 h-3 text-blue-600" />
      : <ArrowDown className="w-3 h-3 text-blue-600" />;
  };

  const filteredAndSortedData = useMemo(() => {
    if (!results) return [];
    let data = [...(results[activeTab] || [])];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(item => item.keyword.toLowerCase().includes(term));
    }

    data.sort((a, b) => {
      const aVal = a[sortConfig.key] ?? 0;
      const bVal = b[sortConfig.key] ?? 0;
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return data;
  }, [results, activeTab, searchTerm, sortConfig]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);
  const paginatedData = filteredAndSortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab, searchTerm]);

  const handleExportCSV = () => {
    if (!filteredAndSortedData.length) return;
    const headers = activeTab === 'common'
      ? ['Keyword', 'Your Position', 'Competitor Position', 'Search Volume', 'CPC', 'Traffic %', 'Competition']
      : activeTab === 'missing'
        ? ['Keyword', 'Competitor Position', 'Search Volume', 'CPC', 'Traffic %', 'Competition']
        : ['Keyword', 'Your Position', 'Search Volume', 'CPC', 'Traffic %', 'Competition'];

    const rows = filteredAndSortedData.map(item => {
      if (activeTab === 'common') {
        return [item.keyword, item.brandPosition, item.competitorPosition, item.searchVolume, item.cpc, item.trafficPercent, item.competition];
      } else if (activeTab === 'missing') {
        return [item.keyword, item.competitorPosition, item.searchVolume, item.cpc, item.trafficPercent, item.competition];
      } else {
        return [item.keyword, item.brandPosition, item.searchVolume, item.cpc, item.trafficPercent, item.competition];
      }
    });

    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keyword-gap-${activeTab}-${results.brandDomain}-vs-${results.competitorDomain}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Keyword Gap Analyser</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Compare organic keywords between your brand and a competitor</p>
      </div>

      {/* Input Form */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-blue-200 dark:border-slate-700 shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          {/* Brand Select */}
          <div>
            <label className="block text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider mb-2">Your Brand *</label>
            <div className="relative">
              <select
                value={selectedBrandUrl}
                onChange={(e) => { setSelectedBrandUrl(e.target.value); setError(''); }}
                disabled={isLoading}
                className="w-full px-4 py-2.5 border border-blue-200 dark:border-slate-600 rounded-lg bg-blue-50 dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-10"
              >
                <option value="">Select a brand...</option>
                {Array.isArray(brands) && brands.length > 0 ? (
                  brands.map((brand, index) => (
                    <option key={index} value={brand.brand_url || brand.name || brand.brandName || brand}>
                      {brand.name || brand.brand_name || brand.brandName || brand}
                    </option>
                  ))
                ) : (
                  <option disabled>Loading brands...</option>
                )}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
            </div>
          </div>

          {/* Competitor URL */}
          <div>
            <label className="block text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider mb-2">Competitor URL</label>
            <input
              type="text"
              placeholder="e.g., competitor.com"
              value={competitorUrl}
              onChange={(e) => { setCompetitorUrl(e.target.value); setError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAnalyze(); }}
              className="w-full px-3 py-2.5 border border-blue-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Database/Country */}
          <div>
            <label className="block text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider mb-2">Country</label>
            <select
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
              className="w-full px-3 py-2.5 border border-blue-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {databases.map(db => (
                <option key={db.code} value={db.code}>{db.name}</option>
              ))}
            </select>
          </div>

          {/* Analyze Button */}
          <div>
            <button
              onClick={handleAnalyze}
              disabled={isLoading || !selectedBrandUrl || !competitorUrl.trim()}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-blue-300 disabled:to-blue-400 text-white font-medium py-2.5 px-6 rounded-lg flex items-center justify-center gap-2 transition-all shadow-sm"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {isLoading ? 'Analyzing...' : 'Analyze Gap'}
            </button>
          </div>
        </div>

        {error && (
          <p className="mt-3 text-sm text-blue-800 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2">{error}</p>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-blue-200 dark:border-slate-700 shadow-sm p-12 flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Fetching keywords from Semrush for both domains...</p>
        </div>
      )}

      {/* Summary Cards */}
      {results && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            { label: 'Your Keywords', count: results.summary.totalBrandKeywords, tab: null, icon: Globe },
            { label: 'Competitor Keywords', count: results.summary.totalCompetitorKeywords, tab: null, icon: Target },
            { label: 'Common', count: results.summary.commonCount, tab: 'common', icon: BarChart3 },
            { label: 'Missing', count: results.summary.missingCount, tab: 'missing', icon: TrendingUp },
            { label: 'Unique to You', count: results.summary.uniqueCount, tab: 'unique', icon: Filter },
          ].map(card => (
            <div
              key={card.label}
              onClick={() => card.tab && setActiveTab(card.tab)}
              className={`bg-white dark:bg-slate-900 rounded-xl border shadow-sm p-4 transition-all ${
                card.tab === activeTab
                  ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800'
                  : 'border-blue-200 dark:border-slate-700'
              } ${card.tab ? 'cursor-pointer hover:shadow-md' : ''}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <card.icon className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{card.label}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(card.count)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Results Table */}
      {results && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-blue-200 dark:border-slate-700 shadow-sm overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-blue-200 dark:border-slate-700">
            {[
              { key: 'common', label: `Common (${results.summary.commonCount})` },
              { key: 'missing', label: `Missing (${results.summary.missingCount})` },
              { key: 'unique', label: `Unique (${results.summary.uniqueCount})` },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}

            {/* Search + Export */}
            <div className="ml-auto flex items-center gap-2 px-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 pr-3 py-1.5 border border-gray-200 dark:border-slate-600 rounded-lg text-xs bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
                />
              </div>
              <button
                onClick={handleExportCSV}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Export CSV"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Tab description */}
          <div className="px-6 py-3 bg-blue-50/50 dark:bg-slate-800/50 border-b border-blue-100 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {activeTab === 'common' && `Keywords both ${results.brandDomain} and ${results.competitorDomain} rank for`}
              {activeTab === 'missing' && `Keywords ${results.competitorDomain} ranks for but ${results.brandDomain} does not — opportunities to target`}
              {activeTab === 'unique' && `Keywords only ${results.brandDomain} ranks for — your competitive advantage`}
            </p>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-50 to-gray-50 dark:from-slate-800 dark:to-slate-800 border-b border-blue-200 dark:border-slate-700">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider w-8">#</th>
                  <th
                    onClick={() => handleSort('keyword')}
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                  >
                    <div className="flex items-center gap-1">Keyword <SortIcon columnKey="keyword" /></div>
                  </th>
                  {activeTab !== 'missing' && (
                    <th
                      onClick={() => handleSort('brandPosition')}
                      className="text-center px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                    >
                      <div className="flex items-center justify-center gap-1">Your Pos. <SortIcon columnKey="brandPosition" /></div>
                    </th>
                  )}
                  {activeTab !== 'unique' && (
                    <th
                      onClick={() => handleSort('competitorPosition')}
                      className="text-center px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                    >
                      <div className="flex items-center justify-center gap-1">Comp. Pos. <SortIcon columnKey="competitorPosition" /></div>
                    </th>
                  )}
                  <th
                    onClick={() => handleSort('searchVolume')}
                    className="text-center px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                  >
                    <div className="flex items-center justify-center gap-1">Volume <SortIcon columnKey="searchVolume" /></div>
                  </th>
                  <th
                    onClick={() => handleSort('cpc')}
                    className="text-center px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                  >
                    <div className="flex items-center justify-center gap-1">CPC <SortIcon columnKey="cpc" /></div>
                  </th>
                  <th
                    onClick={() => handleSort('trafficPercent')}
                    className="text-center px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                  >
                    <div className="flex items-center justify-center gap-1">Traffic % <SortIcon columnKey="trafficPercent" /></div>
                  </th>
                  <th
                    onClick={() => handleSort('competition')}
                    className="text-center px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-blue-600"
                  >
                    <div className="flex items-center justify-center gap-1">Comp. <SortIcon columnKey="competition" /></div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'common' ? 8 : 7} className="text-center py-12 text-sm text-gray-400 dark:text-slate-500">
                      {searchTerm ? 'No keywords match your search' : 'No keywords found in this category'}
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, idx) => (
                    <tr key={item.keyword + idx} className="hover:bg-blue-50/50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="px-4 py-2.5 text-xs text-gray-400">{currentPage * pageSize + idx + 1}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.keyword}</span>
                      </td>
                      {activeTab !== 'missing' && (
                        <td className="px-4 py-2.5 text-center">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            {item.brandPosition ?? '—'}
                          </span>
                        </td>
                      )}
                      {activeTab !== 'unique' && (
                        <td className="px-4 py-2.5 text-center">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {item.competitorPosition ?? '—'}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-2.5 text-center text-sm text-gray-700 dark:text-gray-300 font-medium">
                        {formatNumber(item.searchVolume)}
                      </td>
                      <td className="px-4 py-2.5 text-center text-sm text-gray-600 dark:text-gray-400">
                        ${item.cpc?.toFixed(2) ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 text-center text-sm text-gray-600 dark:text-gray-400">
                        {item.trafficPercent?.toFixed(2) ?? '—'}%
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <div className="w-12 h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${Math.min((item.competition || 0) * 100, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{item.competition?.toFixed(2) ?? '—'}</span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 dark:border-slate-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="px-3 py-1 text-xs rounded-lg border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors"
                >
                  Prev
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i;
                  } else if (currentPage < 3) {
                    page = i;
                  } else if (currentPage > totalPages - 4) {
                    page = totalPages - 5 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      {page + 1}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage === totalPages - 1}
                  className="px-3 py-1 text-xs rounded-lg border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KeywordGapAnalyser;
